import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, Params, Router} from '@angular/router';

import { DataSet } from 'vis-data/peer';
import { Network, Data, Options } from 'vis-network/peer';

import {Question} from 'shared/models/questions/question';
import {QuestionChain} from 'shared/models/questions/question-chain';
import {Tiplist} from 'shared/models/tips/tiplist';
import * as Edge from './interfaces/edge';
import * as Node from './interfaces/node';
import {ModalAddQuestionComponent} from './modal-add-question/modal-add-question.component';
import {ModalEditNodeComponent} from './modal-edit-node/modal-edit-node.component';
import {NetworkService} from './services/network.service';
import {TreeViewService} from './services/tree-view.service';
import { Jump } from './interfaces/jump';

@Component({
  selector: 'tree-view',
  templateUrl: './tree-view.component.html',
  styleUrls: ['./tree-view.component.css'],
})
export class TreeViewComponent implements AfterViewInit {

  @ViewChild('selectModal') private selectModal: ModalAddQuestionComponent;
  @ViewChild('editNodeModal') private editNodeModal: ModalEditNodeComponent;
  public nodes: Node.Set;
  public edges: Edge.Set;
  public network: Network;
  private allNodes: Node.Set;
  private allEdges: Edge.Set;
  private questions: Array<Question>;
  private tipLists: Array<Tiplist>;
  private editNodeCallback: Node.Callback;
  private deleteNodeCallback: Node.Callback;
  public treeTitle: string = '';

  public hoverInfo = '';

  constructor(private treeViewService: TreeViewService,
              private networkService: NetworkService,
              private router: Router,
              private activatedRoute: ActivatedRoute) {

    this.nodes = new DataSet();
    this.edges = new DataSet();
    this.allNodes = new DataSet();
    this.allEdges = new DataSet();
  }

  public ngAfterViewInit(): void {
    const container: HTMLElement = document.getElementById('question-network');
    const data: Data = {nodes: <DataSet <Node.Node>> this.nodes, edges: this.edges};
    const options: Options = {
      autoResize: true,
      height: '100%',
      width: '100%',
      physics: {
        stabilization: {
          iterations: 1000,
          updateInterval: 10,
        },
        timestep: 0.5,
        adaptiveTimestep: true,
        hierarchicalRepulsion: {
          nodeDistance: 100,
        },
      },
      layout: {
        hierarchical: {
          enabled: true,
          parentCentralization: true,
          direction: 'LR',
          sortMethod: 'directed',
          levelSeparation: 350,
        },
        improvedLayout: false,
      },
      manipulation: {
        enabled: false,
        editNode: (nodeData: Node.Node, callback: Node.Callback): void => {
          this.editNodeCallback = callback;
          this.showEditNodeModal(nodeData);
        },
        deleteNode: (nodeData: Node.SelectedElements, callback: Node.Callback): void => {
          this.deleteNodeCallback = callback;
          this.deleteNode(nodeData);
        },
      },
      interaction:{
        hover: true
      },
      edges: {
        labelHighlightBold: true
      }
    };

    this.network = new Network(container, data, options);
    this.network.on('doubleClick', (dblClickParameters: Node.SelectedElements) => {
      if (dblClickParameters.edges.length <= 1) {
        this.network.editNode();
      }
    });

    this.network.on('selectNode', (selectParameters: Node.SelectEvent) => {
      this.networkService.selectNode(selectParameters, this.nodes, this.edges, this.network, this.allNodes, this.allEdges);
    });

    this.network.on('hoverNode', (event: any) => {
      this.hoverInfo = 'Node: '+this.nodes.get(event.node)['label'];
    });

    this.network.on('blurNode', (event: any) => {
      this.hoverInfo = '';
    });

    this.network.on('hoverEdge', (event: any) => {
      this.hoverInfo = 'Edge: '+this.edges.get(event.edge)['label'];
    });

    this.network.on('blurEdge', (event: any) => {
      this.hoverInfo = '';
    });

    this.treeViewService.getAllQuestions().subscribe((questions: Array<Question>) => {
      this.questions = questions;
    });

    this.treeViewService.getAllTipLists().subscribe((tiplists: Array<Tiplist>) => {
      this.tipLists = tiplists;
    });

    this.activatedRoute.params.map((routeParameters: Params) => {
      return routeParameters['treeId'];
    })
      .subscribe((treeId: string) => {
        if (treeId) {
          this.loadTree(treeId);
        }
      });
  }

  private loadTree(id: string): void {
    this.treeViewService.loadTree(id)
      .subscribe((treeToLoad) => {
        this.treeTitle = treeToLoad[0].questions[0].title.de;
        this.networkService.drawExistingTree(treeToLoad, this.nodes, this.edges, this.network, this.allNodes, this.allEdges);
      });
  }

  public goBack(): void {
    this.router.navigateByUrl('treelist');
  }

  public showSelectModal(): void {
    this.selectModal.setQuestions(this.questions)
      .show();
  }

  public showEditNodeModal(nodeData: Node.Node): void {
    this.editNodeModal
      .setQuestions(this.networkService.getUnusedQuestions(nodeData, this.nodes, this.edges, this.questions))
      .setTipLists(this.tipLists)
      .setJumps(this.getJumps())
      .setNodeData(nodeData)
      .show();
  }

  public saveTree(): void {
    this.treeViewService.saveTree(this.allNodes, this.allEdges, this.allNodes.get(this.networkService.rootNode.id))
      .subscribe((res) => {
        this.router.navigateByUrl('treelist');
      });
  }

  public addFirstQuestion(question: Question): void {
    this.networkService.addFirstQuestion(question, this.nodes, this.edges, this.network, this.allNodes, this.allEdges);
  }

  public editNode(eventData: Node.Node): void {
    this.networkService.editNode(eventData, this.editNodeCallback, this.nodes, this.edges, this.network, this.allNodes, this.allEdges);
  }

  private deleteNode(nodeData: Node.SelectedElements): void {
    this.networkService.deleteNode(nodeData, this.nodes, this.edges, this.network, this.allNodes, this.allEdges);
  }

  // get possiple jump targets (not the already asigned jumps)
  private getJumps(): Array<Jump> {
    const jumps = [];
    this.nodes.getIds().forEach((id) => {
      const data = this.nodes.get(id).data;
      if ( data !== undefined && Node.Tools.isQuestion(data)) {
        jumps.push(new Jump(id+'', <Question>data));
      }
    });
    return jumps;
  }
}



// WEBPACK FOOTER //
// /usr/src/app/src/app/modules/dynamic-tips/tree-view/tree-view.component.ts